import React, { useState } from 'react';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';

const PaymentConfigurationStep = ({ formData, updateFormData, onNext, onPrevious }) => {
  const [errors, setErrors] = useState({});
  const [receiptPreview, setReceiptPreview] = useState(null);

  const paymentMethods = [
    {
      id: 'nequi',
      name: 'Nequi',
      icon: 'Smartphone',
      color: 'bg-purple-500',
      instructions: 'Realiza la transferencia al número 315 249 5959 a nombre de BIZZNET SAS'
    },
    {
      id: 'bancolombia',
      name: 'Bancolombia',
      icon: 'Building2',
      color: 'bg-yellow-500',
      instructions: 'Cuenta de Ahorros No. 912-898658-41 a nombre de BIZZNET SAS'
    }
  ];

  const handleFileUpload = (e) => {
    const file = e?.target?.files?.[0];
    if (file) {
      if (file?.size > 5 * 1024 * 1024) {
        setErrors({ ...errors, paymentReceipt: 'El archivo no debe superar 5MB' });
        return;
      }

      if (!['image/jpeg', 'image/png', 'image/jpg', 'application/pdf']?.includes(file?.type)) {
        setErrors({ ...errors, paymentReceipt: 'Solo se permiten archivos JPG, PNG o PDF' });
        return;
      }

      updateFormData({ paymentReceipt: file });
      
      if (file?.type?.startsWith('image/')) {
        const reader = new FileReader();
        reader.onloadend = () => {
          setReceiptPreview(reader?.result);
        };
        reader?.readAsDataURL(file);
      } else {
        setReceiptPreview(null);
      }

      setErrors({ ...errors, paymentReceipt: null });
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.paymentMethod) {
      newErrors.paymentMethod = 'Selecciona un método de pago';
    }

    if (!formData?.transactionReference?.trim()) {
      newErrors.transactionReference = 'El número de referencia es requerido';
    }

    if (!formData?.paymentReceipt) {
      newErrors.paymentReceipt = 'Debes subir el comprobante de pago';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleContinue = () => {
    if (validateForm()) {
      onNext();
    }
  };

  const selectedMethod = paymentMethods?.find(m => m?.id === formData?.paymentMethod);

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-foreground mb-2">Configuración de Pago</h2>
        <p className="text-muted-foreground">Completa tu pago para activar tu membresía</p>
      </div>
      {formData?.selectedPlan && (
        <div className="bg-gradient-to-r from-primary/10 to-accent/10 border-2 border-primary/20 rounded-xl p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground mb-1">Plan Seleccionado</p>
              <p className="text-2xl font-bold text-foreground">{formData?.selectedPlan?.name}</p>
            </div>
            <div className="text-right">
              <p className="text-sm text-muted-foreground mb-1">Total a Pagar</p>
              <p className="text-3xl font-extrabold bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
                {formData?.selectedPlan?.priceFormatted} COP
              </p>
            </div>
          </div>
        </div>
      )}
      <div>
        <label className="block text-sm font-medium text-foreground mb-3">
          Selecciona tu método de pago <span className="text-destructive">*</span>
        </label>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {paymentMethods?.map((method) => (
            <div
              key={method?.id}
              onClick={() => updateFormData({ paymentMethod: method?.id })}
              className={`relative border-3 rounded-xl p-4 cursor-pointer transition-all duration-300 ${
                formData?.paymentMethod === method?.id
                  ? 'border-primary bg-primary/5 shadow-lg transform scale-105'
                  : 'border-gray-200 hover:border-gray-300 hover:shadow-md'
              }`}
            >
              {formData?.paymentMethod === method?.id && (
                <div className="absolute top-2 right-2 bg-primary text-white rounded-full p-1">
                  <Icon name="Check" size={16} />
                </div>
              )}
              <div className="flex items-center gap-3">
                <div className={`${method?.color} text-white rounded-lg p-3 shadow-md`}>
                  <Icon name={method?.icon} size={24} />
                </div>
                <div className="flex-1">
                  <p className="font-bold text-foreground">{method?.name}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
        {errors?.paymentMethod && (
          <p className="text-sm text-destructive mt-2 flex items-center gap-1">
            <Icon name="AlertCircle" size={14} />
            {errors?.paymentMethod}
          </p>
        )}
      </div>
      {selectedMethod && (
        <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-6 space-y-4">
          <div className="flex items-start gap-3">
            <Icon name="Info" size={20} className="text-blue-600 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-sm font-bold text-blue-900 mb-2">Instrucciones de Pago</p>
              <p className="text-sm text-blue-800">{selectedMethod?.instructions}</p>
            </div>
          </div>

          <div className="bg-white rounded-lg p-4">
            <p className="text-sm font-semibold text-foreground mb-2">Pasos a seguir:</p>
            <ol className="text-sm text-muted-foreground space-y-2 list-decimal list-inside">
              <li>Realiza la transferencia por el monto exacto de {formData?.selectedPlan?.priceFormatted} COP</li>
              <li>Guarda el comprobante de pago o captura de pantalla</li>
              <li>Anota el número de referencia o transacción</li>
              <li>Sube el comprobante en el siguiente campo</li>
            </ol>
          </div>
        </div>
      )}
      <Input
        label="Número de Referencia / Transacción"
        required
        placeholder="Ej: REF123456789"
        value={formData?.transactionReference || ''}
        onChange={(e) => updateFormData({ transactionReference: e?.target?.value })}
        error={errors?.transactionReference}
        description="Ingresa el número de referencia que aparece en tu comprobante"
      />
      <div className="space-y-3">
        <label className="block text-sm font-medium text-foreground">
          Comprobante de Pago <span className="text-destructive">*</span>
        </label>
        
        <div className="border-3 border-dashed border-gray-300 rounded-xl p-6 text-center hover:border-primary transition-colors cursor-pointer relative">
          <input
            type="file"
            accept="image/jpeg,image/png,image/jpg,application/pdf"
            onChange={handleFileUpload}
            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
          />
          
          {formData?.paymentReceipt ? (
            <div className="space-y-3">
              {receiptPreview && (
                <img
                  src={receiptPreview}
                  alt="Vista previa del comprobante"
                  className="max-h-48 mx-auto rounded-lg shadow-md"
                />
              )}
              <div className="flex items-center justify-center gap-2 text-green-600">
                <Icon name="CheckCircle" size={20} />
                <span className="font-semibold">{formData?.paymentReceipt?.name}</span>
              </div>
              <p className="text-sm text-muted-foreground">
                Haz clic para cambiar el archivo
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="flex justify-center">
                <div className="bg-primary/10 rounded-full p-4">
                  <Icon name="Upload" size={32} className="text-primary" />
                </div>
              </div>
              <div>
                <p className="font-semibold text-foreground">Arrastra tu archivo aquí</p>
                <p className="text-sm text-muted-foreground mt-1">
                  o haz clic para seleccionar
                </p>
              </div>
              <p className="text-xs text-muted-foreground">
                JPG, PNG o PDF (máx. 5MB)
              </p>
            </div>
          )}
        </div>

        {errors?.paymentReceipt && (
          <p className="text-sm text-destructive flex items-center gap-1">
            <Icon name="AlertCircle" size={14} />
            {errors?.paymentReceipt}
          </p>
        )}
      </div>
      <div className="bg-yellow-50 border-2 border-yellow-200 rounded-xl p-4">
        <div className="flex items-start gap-3">
          <Icon name="AlertTriangle" size={20} className="text-yellow-600 mt-0.5 flex-shrink-0" />
          <div className="text-sm text-yellow-900">
            <p className="font-semibold mb-1">Importante</p>
            <ul className="space-y-1 list-disc list-inside">
              <li>Tu cuenta será activada dentro de las próximas 24-48 horas hábiles</li>
              <li>Recibirás un correo de confirmación una vez verificado el pago</li>
              <li>Asegúrate de que el monto coincida exactamente con el plan seleccionado</li>
            </ul>
          </div>
        </div>
      </div>
      <div className="flex gap-4 pt-6 border-t">
        <Button
          variant="outline"
          size="lg"
          onClick={onPrevious}
          iconName="ArrowLeft"
          iconPosition="left"
          className="flex-1"
        >
          Regresar
        </Button>

        <Button
          variant="default"
          size="lg"
          onClick={handleContinue}
          iconName="ArrowRight"
          iconPosition="right"
          className="flex-1 bg-gradient-to-r from-primary to-accent hover:shadow-lg transform hover:scale-105 transition-all"
        >
          Continuar a Verificación
        </Button>
      </div>
    </div>
  );
};

export default PaymentConfigurationStep;